import React, { useState, useEffect } from 'react';
import { MobileStepper, Button } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import BaseModal from '../baseModal';
import './style.scss';

const LaunchTokenModal = ({ show, close }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    symbol: '',
    image: null,
    deflationRate: 'Halving',
    fee: 5,
    minMinerReward: '',
  });

  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);

  const steps = 3;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      const file = files[0];
      if (file && file.type === 'image/png') {
        setFormData({ ...formData, [name]: file });
        setErrors({ ...errors, image: '' });
      } else {
        setErrors({ ...errors, image: 'Please upload a 256x256 PNG file.' });
      }
    } else {
      setFormData({ ...formData, [name]: value });
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!/^[A-Za-z0-9]{3,13}$/.test(formData.name)) {
      validationErrors.name = 'Token Name must be 3-13 ASCII letters and numbers only.';
    }
    if (!/^[A-Za-z0-9\s]{3,42}$/.test(formData.description)) {
      validationErrors.description =
        'Token Description must be 3-42 ASCII letters, numbers, and spaces only.';
    }
    if (!/^[A-Za-z0-9]{3,4}$/.test(formData.symbol)) {
      validationErrors.symbol = 'Token Symbol must be 3-4 ASCII letters and numbers only.';
    }
    if (!formData.image || formData.image.type !== 'image/png') {
      validationErrors.image = 'Please upload a valid PNG image file.';
    }
    if (!formData.minMinerReward || formData.minMinerReward < 1 || formData.minMinerReward > 1000) {
      validationErrors.minMinerReward = 'Minimum Miner Reward must be between 1 and 1000.';
    }
    setErrors(validationErrors);
    setIsFormValid(Object.keys(validationErrors).length === 0);
  };

  useEffect(() => {
    if (activeStep === 0) {
      validateForm();
    }
  }, [formData, activeStep]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid) {
      alert('Form submitted with data: ' + JSON.stringify(formData));
      close();
    } else {
      validateForm();
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !isFormValid) {
      validateForm();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <form onSubmit={handleSubmit} className="launch-token-form">
            <div className="form-group">
              <label htmlFor="name">Token Name</label>
              <input
                type="text"
                id="token-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter token name"
                maxLength="13"
                required
              />
              {errors.name && <p className="error">{errors.name}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Token Description</label>
              <input
                id="token-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter token description"
                maxLength="42"
                required
              />
              {errors.description && <p className="error">{errors.description}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="symbol">Token Symbol</label>
              <input
                type="text"
                id="token-symbol"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                placeholder="Enter token symbol"
                maxLength="4"
                required
              />
              {errors.symbol && <p className="error">{errors.symbol}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="image">Token Image (256x256 PNG)</label>
              <input
                type="file"
                id="token-image"
                name="image"
                accept=".png"
                onChange={handleChange}
                required
              />
              {errors.image && <p className="error">{errors.image}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="deflationRate">Deflation Rate</label>
              <select
                id="token-deflationRate"
                name="deflationRate"
                value={formData.deflationRate}
                onChange={handleChange}
              >
                <option value="Halving">Halving</option>
                <option value="Quartering">Quartering</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="minMinerReward">Minimum Miner Reward</label>
              <input
                type="number"
                id="token-minMinerReward"
                name="minMinerReward"
                value={formData.minMinerReward}
                onChange={handleChange}
                placeholder="Enter minimum miner reward"
                min="1"
                max="1000"
                required
              />
              {errors.minMinerReward && <p className="error">{errors.minMinerReward}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="fee">Mining Fee: {formData.fee}%</label>
              <input
                type="range"
                id="token-fee"
                name="fee"
                min="1"
                max="30"
                value={formData.fee}
                onChange={handleChange}
              />
            </div>

          </form>
        );
      case 1:
        return <p>Page 2: Additional information or configuration here...</p>;
      case 2:
        return <p>Page 3: Review and confirm your token details here...</p>;
      default:
        return null;
    }
  };

  return (
    <BaseModal show={show} handleClose={close} title={'Launch New Token'}>
      {renderStepContent(activeStep)}
      <MobileStepper
        variant="dots"
        steps={steps}
        position="static"
        activeStep={activeStep}
        nextButton={
          <Button
            size="small"
            onClick={handleNext}
            disabled={(activeStep === 0 && !isFormValid) || activeStep === steps - 1}
          >
            Next
            <KeyboardArrowRight />
          </Button>
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <KeyboardArrowLeft />
            Back
          </Button>
        }
      />
    </BaseModal>
  );
};

export default LaunchTokenModal;



// Token Name: [used in metadata.json, as well as used in the literal file names and refs to contracts in contract calls between contracts]
// Token Description: [used in metadata.json]
// Token Symbol:  [used in their contract as symbol and effectively how we use CC in our .cryptocash-token contract]
// Token Image:   [full path set in metadata.json]
// Deflation Schedule: Halving or Quartering [used in .cryptocash-core contract]
// Mining Fee: 5%-30%  [used in .cryptocash-core contract]
// Initial Reward per Mined Block:   [used in .cryptocash-core contract]

// (we have user fill that out ^ then we store the metadata.json and token image in the users Stacks Storage)
// (we then make sure their contracts uses the stored metadata.json full path)
// (we charge a small 1 time fee to launch it and have it integrated into our merged pox-lite extension)

// user has to then deploy their contracts in this order:
// x-vrf.clar
// x-token-trait.clar
// x-core.trait.clar
// x-token.clar
// x-core.clar